/**
 * Posting to GitHub. The only module in this product that writes to a shared surface.
 *
 * ⛔ ONE `POST /pulls/{n}/reviews` CARRYING EVERY ACCEPTED COMMENT — one request, not twenty. There
 * is a documented limit of 80 content-creating requests per minute, and a review with twenty
 * comments is one request against it while twenty comment calls are twenty, plus twenty
 * notifications, plus twenty threads.
 *
 * ⛔ ANCHORING IS ALL-OR-NOTHING. One comment on a line outside the diff fails the entire call with
 * a 422 and NOTHING lands — not the other nineteen comments, not the summary. `partitionByAnchor`
 * runs before anything is built, and whatever cannot anchor is folded into the summary by name
 * rather than being allowed to sink the batch.
 *
 * ⛔ THREE LOCKS, CHECKED BEFORE ANY NETWORK CALL: `post.enabled`, `post.dry_run` and
 * `triage.require_human`. See docs/spec/config.md, `[post]` and `[triage]`.
 *
 * ⛔ THE COMMIT STATUS IS THE BLOCKING MECHANISM, because a personal access token cannot create a
 * check run — "You must authenticate via a GitHub App", measured. HIGH ⇒ `failure`, otherwise
 * `success`. On an INTERNAL error it sets nothing at all: a broken reviewer must not fail someone
 * else's build, and our outage is not their defect.
 */

import type { SibylConfig, CommitStatusState } from "../config/schema.js"
import type { Finding, TriageState } from "../types/finding.js"
import { GitHub, GitHubError, partitionByAnchor, type AnchorSet } from "./pr.js"

/** Everything posting reads out of the resolved configuration, and nothing else. */
export type PostConfig = Pick<SibylConfig, "post" | "triage" | "comments" | "severity">

export class PostError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "PostError"
  }
}

// ── The mention guard ──────────────────────────────────────────────────────────────────────────

/**
 * ⛔ `(?<![\w.])@\w`, NEVER a bare `@\w`.
 *
 * A bare pattern false-positives on every email address in a stack trace and on a `branch @ sha`
 * metadata line, so it either mangles ordinary prose or gets turned off. The lookbehind is what
 * makes the guard survivable. An at-mention re-fires a notification on every edit of the comment
 * that contains it, which is why this is a hard rule rather than a preference.
 */
export const MENTION_PATTERN = /(?<![\w.])@\w/g

export function containsMention(body: string): boolean {
  // A global regex carries lastIndex, so it is rebuilt rather than shared with the replacer.
  return new RegExp(MENTION_PATTERN.source).test(body)
}

/**
 * Neutralise every at-mention without mangling the text a person wrote.
 *
 * `&#64;` renders as the same character on GitHub and creates no notification — so the comment
 * reads exactly as written and nobody is paged by a machine.
 */
export function sanitiseMentions(body: string): string {
  return body.replace(MENTION_PATTERN, (m) => `&#64;${m.slice(1)}`)
}

// ── What goes on the wire ──────────────────────────────────────────────────────────────────────

export interface ReviewComment {
  readonly path: string
  readonly line: number
  readonly side: "LEFT" | "RIGHT"
  readonly start_line?: number
  readonly start_side?: "LEFT" | "RIGHT"
  readonly body: string
}

export interface ReviewRequestBody {
  readonly commit_id: string
  readonly body: string
  /** ⛔ COMMENT and nothing else. A machine does not approve and does not request changes under a
   * human's token — and our token is usually the pull request's own author, who cannot approve it
   * anyway. Blocking is the commit status's job. */
  readonly event: "COMMENT"
  readonly comments: readonly ReviewComment[]
}

export interface CommitStatusRequestBody {
  readonly state: "success" | "failure" | "pending" | "error"
  readonly context: string
  readonly description: string
  readonly target_url?: string
}

/** A finding that could not be posted at its own anchor, with the reason, never silently dropped. */
export interface ExcludedComment {
  readonly fingerprint: string
  readonly path: string
  readonly line: number
  readonly reason: string
}

export interface ReviewPlan {
  readonly body: ReviewRequestBody
  readonly excluded: readonly ExcludedComment[]
  /** What the commit status will be set to if this plan is posted. */
  readonly commitStatus: CommitStatusState
}

/** A finding together with the triage decision that authorises it. ⛔ Nothing posts without one. */
export interface PostableFinding {
  readonly finding: Finding
  readonly state: TriageState
}

// ── The locks ──────────────────────────────────────────────────────────────────────────────────

export type LockId = "post.enabled" | "post.dry_run" | "triage.require_human" | "post.confirm_token"

export interface EngagedLock {
  readonly id: LockId
  readonly detail: string
}

/**
 * Which locks are engaged right now.
 *
 * ⚠️ `triage.require_human = true` is not by itself a refusal — docs/spec/config.md calls
 * `post.enabled` together with `require_human` "the intended steady state". What the master switch
 * forbids is a finding reaching GitHub that no person accepted, so the lock reads the decisions
 * rather than the flag: every item must carry an `accepted` state, and the ones that do not are
 * named. A run where a person accepted everything posts; a run with one un-triaged finding does
 * not, and says which.
 */
export function engagedLocks(
  cfg: PostConfig,
  items: readonly PostableFinding[],
  confirm?: string,
): EngagedLock[] {
  const locks: EngagedLock[] = []
  if (!cfg.post.enabled) {
    locks.push({ id: "post.enabled", detail: "post.enabled is false — nothing reaches GitHub." })
  }
  if (cfg.post.dryRun) {
    locks.push({
      id: "post.dry_run",
      detail: "post.dry_run is true — the exact request body is rendered and nothing is sent.",
    })
  }
  if (cfg.triage.requireHuman) {
    // ⛔ UNDECIDED, NOT UN-ACCEPTED. This read `!== "accepted"`, which counts a DISMISSED finding as
    // blocking — but a dismissal is a human decision, and the most explicit one there is. The effect
    // was that dismissing a finding locked the post permanently: the lock could only be cleared by
    // accepting something a person had deliberately rejected. What this gate exists to catch is a
    // finding NOBODY has answered.
    const unaccepted = items.filter((i) => i.state.kind === "todo")
    if (unaccepted.length > 0) {
      const names = unaccepted.map((i) => i.finding.fingerprint).join(", ")
      locks.push({
        id: "triage.require_human",
        detail: `triage.require_human is true and ${unaccepted.length} of ${items.length} findings carry no human acceptance: ${names}`,
      })
    }
  }
  // Documented in docs/spec/config.md `[post].confirm_token`: a wrong token downgrades to dry run
  // and PRINTS that, rather than failing silently or, worse, posting anyway.
  if (cfg.post.confirmToken !== "" && confirm !== cfg.post.confirmToken) {
    locks.push({
      id: "post.confirm_token",
      detail: "post.confirm_token is set and --confirm did not match it literally.",
    })
  }
  return locks
}

// ── Building the review ────────────────────────────────────────────────────────────────────────

export interface BuildInput {
  readonly reviewedSha: string
  readonly summary: string
  readonly anchorSet: AnchorSet
  readonly items: readonly PostableFinding[]
  /** Used in the comment signature so we can find and update our own comments later. */
  readonly reviewId?: string
}

function signatureFor(cfg: PostConfig, reviewId: string, fingerprint: string): string {
  return cfg.comments.signature
    .replace("{review_id}", reviewId)
    .replace("{finding_id}", fingerprint)
}

function commentBody(cfg: PostConfig, f: Finding, reviewId: string): string {
  const parts: string[] = []
  parts.push(`**${f.severity.toUpperCase()}** · ${f.dimensionId}`)
  parts.push(f.title)
  parts.push(f.consequence)
  if (f.failureScenario !== "") parts.push(`**How to reproduce:** ${f.failureScenario}`)
  if (f.recommendedFix !== "") parts.push(`**Fix (${f.effort}):** ${f.recommendedFix}`)
  if (f.suggestion !== undefined) {
    parts.push(["```suggestion", ...f.suggestion.replacementLines, "```"].join("\n"))
  }
  parts.push(signatureFor(cfg, reviewId, f.fingerprint))
  return parts.join("\n\n")
}

function toComment(cfg: PostConfig, f: Finding, reviewId: string): ReviewComment {
  const base = {
    path: f.anchor.path,
    line: f.anchor.line,
    side: f.anchor.side,
    body: commentBody(cfg, f, reviewId),
  }
  // ⚠️ GitHub requires BOTH ends of a multi-line anchor or neither. Sending one is a 422, and a 422
  // here loses the whole review.
  if (f.anchor.startLine !== undefined && f.anchor.startSide !== undefined) {
    return { ...base, start_line: f.anchor.startLine, start_side: f.anchor.startSide }
  }
  return base
}

/**
 * Apply the `[comments]` caps.
 *
 * ⭐ Attention is the scarce resource, not correctness: past roughly 25 inline comments a review is
 * read as noise and closed unread. Everything cut is rolled into the summary with an exact count —
 * `overflow = "rollup"` — so nothing is lost, it is just not shouted.
 */
function applyCaps(
  cfg: PostConfig,
  anchored: readonly Finding[],
): { kept: Finding[]; overflow: Finding[] } {
  const kept: Finding[] = []
  const overflow: Finding[] = []
  const perFile = new Map<string, number>()
  const perDimension = new Map<string, number>()
  const perLine = new Map<string, number>()

  for (const f of anchored) {
    const lineKey = `${f.anchor.path}:${f.anchor.side}:${f.anchor.line}`
    const fileCount = perFile.get(f.anchor.path) ?? 0
    const dimCount = perDimension.get(f.dimensionId) ?? 0
    const lineCount = perLine.get(lineKey) ?? 0
    const over =
      kept.length >= cfg.comments.maxPerReview ||
      fileCount >= cfg.comments.maxPerFile ||
      dimCount >= cfg.comments.maxPerDimension ||
      lineCount >= cfg.comments.maxPerLine
    if (over) {
      overflow.push(f)
      continue
    }
    kept.push(f)
    perFile.set(f.anchor.path, fileCount + 1)
    perDimension.set(f.dimensionId, dimCount + 1)
    perLine.set(lineKey, lineCount + 1)
  }
  return { kept, overflow }
}

/** Findings that may be commented on at all, before anchoring is considered. */
function commentable(cfg: PostConfig, items: readonly PostableFinding[]): Finding[] {
  return items
    .map((i) => i.finding)
    .filter((f) => {
      // ⛔ A pull request author is not accountable for code they did not touch. Commenting on
      // pre-existing findings is how a review becomes something people route around.
      if (f.cls === "pre_existing" && !cfg.severity.preExisting.comment) return false
      // ⛔ `shadow` findings come from a dimension that is switched off. Visible in triage, never
      // postable — that is the entire meaning of the class.
      if (f.cls === "shadow") return false
      return cfg.severity.actions[f.severity].comment
    })
}

/** HIGH blocks. Nothing else does. Pre-existing never blocks, whatever its severity says. */
export function blockingFindings(cfg: PostConfig, items: readonly PostableFinding[]): Finding[] {
  return items
    .map((i) => i.finding)
    .filter(
      (f) =>
        f.severity === "high" &&
        f.cls === "introduced" &&
        cfg.severity.actions.high.blocksMerge !== false,
    )
}

/**
 * Render the exact request body, with nothing sent.
 *
 * ⛔ `partitionByAnchor` FIRST. Everything downstream assumes every comment in `body.comments` is
 * on a line GitHub will accept, because the alternative is a 422 that drops the whole review.
 */
export function buildReview(cfg: PostConfig, input: BuildInput): ReviewPlan {
  // ⛔ A runtime floor, not a type assertion. `reviewEvent` is typed `"COMMENT"` but arrives from a
  // TOML file through a cast, and an exhaustive union has no runtime floor — a value outside it
  // would otherwise reach the wire and APPROVE somebody's pull request under a human's token.
  if ((cfg.post.reviewEvent as string) !== "COMMENT") {
    throw new PostError(
      `post.review_event must be COMMENT; found ${String(cfg.post.reviewEvent)}. A machine does not approve and does not request changes.`,
    )
  }

  const reviewId = input.reviewId ?? input.reviewedSha.slice(0, 12)
  const candidates = commentable(cfg, input.items)
  const { anchored, unanchorable } = partitionByAnchor(input.anchorSet, candidates)
  const { kept, overflow } = applyCaps(cfg, anchored)

  const excluded: ExcludedComment[] = [
    ...unanchorable.map((f) => ({
      fingerprint: f.fingerprint,
      path: f.anchor.path,
      line: f.anchor.line,
      reason: "the cited line is not in this pull request's diff",
    })),
    ...overflow.map((f) => ({
      fingerprint: f.fingerprint,
      path: f.anchor.path,
      line: f.anchor.line,
      reason: `over the ${cfg.comments.overflow} cap for this review`,
    })),
  ]

  const comments = kept.map((f) => toComment(cfg, f, reviewId))
  const blocking = blockingFindings(cfg, input.items)
  const commitStatus: CommitStatusState =
    blocking.length > 0
      ? cfg.severity.actions.high.commitStatus
      : input.items.length === 0
        ? cfg.post.commitStatus.onNoFindings
        : "success"

  const body = renderSummary(cfg, input, kept.length, excluded, blocking.length)
  const guard = cfg.comments.mentionUsers ? (s: string) => s : sanitiseMentions

  return {
    body: {
      commit_id: input.reviewedSha,
      body: guard(body),
      event: "COMMENT",
      comments: comments.map((c) => ({ ...c, body: guard(c.body) })),
    },
    excluded,
    commitStatus,
  }
}

/**
 * ⭐ "Reviewed, found nothing" is a RESULT. Silence is indistinguishable from a crashed run, which
 * is why `summary_on_empty` defaults to true and why the excluded findings are counted here rather
 * than dropped.
 */
function renderSummary(
  cfg: PostConfig,
  input: BuildInput,
  postedCount: number,
  excluded: readonly ExcludedComment[],
  blockingCount: number,
): string {
  // ⚠️ A review body is REQUIRED by the API when the event is COMMENT, so `summary_comment = false`
  // cannot mean "send no body" — it means "send the counts and nothing else".
  const lines: string[] = cfg.comments.summaryComment ? [input.summary.trim()] : []
  lines.push(
    `${postedCount} comment${postedCount === 1 ? "" : "s"} on this review · ${blockingCount} blocking.`,
  )
  if (excluded.length > 0) {
    // ⛔ Named with an exact count, never silently lost. A comment that could not be anchored is
    // still a finding a person accepted, and the author cannot act on what they are not told.
    lines.push(
      [
        `<details><summary>${excluded.length} finding${excluded.length === 1 ? "" : "s"} not shown inline</summary>`,
        "",
        ...excluded.map((e) => `- \`${e.path}:${e.line}\` — ${e.reason}`),
        "",
        "</details>",
      ].join("\n"),
    )
  }
  return lines.join("\n\n")
}

// ── Talking to GitHub ──────────────────────────────────────────────────────────────────────────

export interface PostOptions {
  readonly repo: string
  readonly pullNumber: number
  readonly token: string
  readonly baseUrl?: string
  /** ⛔ Injected everywhere. No test in this repository reaches the network. */
  readonly fetchImpl?: typeof fetch
  /** The sha the review was computed against. If head has moved, nothing posts. */
  readonly reviewedSha: string
  readonly summary: string
  readonly anchorSet: AnchorSet
  readonly items: readonly PostableFinding[]
  readonly reviewId?: string
  readonly confirm?: string
  readonly targetUrl?: string
}

export type PostResult =
  | {
      readonly kind: "refused"
      readonly locks: readonly EngagedLock[]
      readonly plan: ReviewPlan
      readonly contentRequests: 0
    }
  | {
      readonly kind: "aborted"
      readonly reason: string
      readonly expectedSha: string
      readonly actualSha: string
      readonly plan: ReviewPlan
      readonly contentRequests: 0
    }
  | {
      readonly kind: "posted"
      readonly plan: ReviewPlan
      readonly reviewId: number
      readonly commitStatus: CommitStatusState
      readonly contentRequests: number
    }
  | {
      readonly kind: "failed"
      readonly error: string
      readonly commitStatus: CommitStatusState
      readonly contentRequests: number
    }
  /**
   * Nothing was accepted and `comments.summary_on_empty` is off, so no review was created. The
   * commit status is still set — the author's build must not sit pending because we had nothing to
   * say.
   */
  | {
      readonly kind: "nothing_to_post"
      readonly plan: ReviewPlan
      readonly commitStatus: CommitStatusState
      readonly contentRequests: number
    }

interface Wire {
  readonly token: string
  readonly baseUrl?: string
  readonly fetchImpl?: typeof fetch
}

async function ghPost(wire: Wire, path: string, body: unknown): Promise<Record<string, unknown>> {
  const f = wire.fetchImpl ?? fetch
  const base = wire.baseUrl ?? "https://api.github.com"
  const res = await f(`${base}${path}`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${wire.token}`,
      accept: "application/vnd.github+json",
      "content-type": "application/json",
      "user-agent": "sibyl",
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new GitHubError(res.status, await res.text())
  return (await res.json()) as Record<string, unknown>
}

/**
 * One commit status. ⛔ The only blocking mechanism available to a personal access token.
 *
 * Returns false when nothing was sent, which is a legitimate outcome and not an error — a status of
 * `none` is the configured way to say "do not touch this author's build".
 */
export async function setCommitStatus(
  cfg: PostConfig,
  opts: Wire & { readonly repo: string; readonly sha: string; readonly targetUrl?: string },
  state: CommitStatusState,
  description: string,
): Promise<boolean> {
  if (!cfg.post.commitStatus.enabled) return false
  if (state === "none") return false
  // ⚠️ `target_url_base` is empty by default ON PURPOSE: the natural value is the local console,
  // and a http://127.0.0.1 link in a shared pull request is dead for everyone but its author.
  const target = opts.targetUrl ?? cfg.post.commitStatus.targetUrlBase
  const body: CommitStatusRequestBody = {
    state,
    context: cfg.post.commitStatus.context,
    // GitHub truncates at 140 characters; truncating here means the text we log is the text sent.
    description: description.slice(0, 140),
    ...(target === "" ? {} : { target_url: target }),
  }
  await ghPost(opts, `/repos/${opts.repo}/statuses/${opts.sha}`, body)
  return true
}

/**
 * Post the review.
 *
 * Order matters and is the whole design:
 *   1. locks — pure, no I/O, so a locked run cannot touch the network by any path;
 *   2. plan  — pure, so a dry run renders exactly what a real run would send;
 *   3. head sha — one GET, which is not a content-creating request;
 *   4. ONE review — the only content-creating request carrying comments;
 *   5. ONE commit status.
 */
export async function postReview(cfg: PostConfig, opts: PostOptions): Promise<PostResult> {
  const locks = engagedLocks(cfg, opts.items, opts.confirm)
  const plan = buildReview(cfg, {
    reviewedSha: opts.reviewedSha,
    summary: opts.summary,
    anchorSet: opts.anchorSet,
    items: opts.items,
    ...(opts.reviewId === undefined ? {} : { reviewId: opts.reviewId }),
  })
  if (locks.length > 0) return { kind: "refused", locks, plan, contentRequests: 0 }

  const wire: Wire = {
    token: opts.token,
    ...(opts.baseUrl === undefined ? {} : { baseUrl: opts.baseUrl }),
    ...(opts.fetchImpl === undefined ? {} : { fetchImpl: opts.fetchImpl }),
  }
  let contentRequests = 0
  try {
    const gh = new GitHub(wire)
    const pr = await gh.pullRequest(opts.repo, opts.pullNumber)
    if (pr.headSha !== opts.reviewedSha) {
      if (cfg.post.onConflict !== "force") {
        // ⛔ Posting a review of code that is no longer there is worse than not posting. Every
        // anchor was computed against a diff that has moved, so the comments would land on lines
        // that now say something else.
        return {
          kind: "aborted",
          reason:
            cfg.post.onConflict === "repost"
              ? "the head sha moved; `repost` needs a fresh review against the new head, so nothing was sent"
              : "the head sha moved between the review and the post",
          expectedSha: opts.reviewedSha,
          actualSha: pr.headSha,
          plan,
          contentRequests: 0,
        }
      }
    }

    const nothingToSay =
      opts.items.length === 0 && plan.body.comments.length === 0 && !cfg.comments.summaryOnEmpty

    // ⛔ ONE REQUEST CARRYING EVERY COMMENT. Twenty comments is twenty requests against the
    // documented 80-per-minute content limit, twenty notifications and twenty threads; one review
    // is one of each. This is the only call in the product that creates review comments.
    const created = nothingToSay
      ? undefined
      : await ghPost(wire, `/repos/${opts.repo}/pulls/${opts.pullNumber}/reviews`, plan.body)
    if (created !== undefined) contentRequests++
    const rawId: unknown = created === undefined ? undefined : created["id"]
    const reviewId = typeof rawId === "number" ? rawId : 0

    const description =
      plan.commitStatus === "failure"
        ? `${blockingFindings(cfg, opts.items).length} blocking finding(s)`
        : `${plan.body.comments.length} comment(s), none blocking`
    const sent = await setCommitStatus(
      cfg,
      {
        ...wire,
        repo: opts.repo,
        sha: opts.reviewedSha,
        ...(opts.targetUrl === undefined ? {} : { targetUrl: opts.targetUrl }),
      },
      plan.commitStatus,
      description,
    )
    if (sent) contentRequests++

    if (created === undefined) {
      return { kind: "nothing_to_post", plan, commitStatus: plan.commitStatus, contentRequests }
    }
    return { kind: "posted", plan, reviewId, commitStatus: plan.commitStatus, contentRequests }
  } catch (e) {
    // ⛔ A BROKEN REVIEWER MUST NOT FAIL SOMEONE'S BUILD. On an internal error the default is to set
    // NOTHING: our outage is not their defect, and a red mark nobody can explain is what makes a
    // team route around a reviewer. The run report and the exit code carry the failure instead.
    const onError = cfg.post.commitStatus.onError
    if (onError !== "none") {
      try {
        const sent = await setCommitStatus(
          cfg,
          { ...wire, repo: opts.repo, sha: opts.reviewedSha },
          onError,
          "sibyl could not complete this review",
        )
        if (sent) contentRequests++
      } catch {
        /* a status we could not set is not worth a second failure */
      }
    }
    return {
      kind: "failed",
      error: e instanceof Error ? e.message : String(e),
      commitStatus: onError,
      contentRequests,
    }
  }
}

/** Re-exported so a caller never rebuilds the legal anchor set with a second, different rule. */
export type { AnchorSet }
